'use client';
import { FC, useCallback, useMemo, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import PreviewImageItems from '@/widgets/PreviewImageItems/ui/PreviewImageItems';
import useQueryParam from '@/shared/hooks/useQueryParam';
import Tag from '@/shared/ui/Tag/Tag';
import Tags from '@/entities/Tags/Tags';
import { PopularTypes } from '@/shared/api/popular/getPopular/types';
import { useGetPopular } from '@/shared/hooks/useGetPopular';
import Layout from '@/widgets/Layout/Layout';
import HeaderButtonsDefault from '@/widgets/Header/ui/HeaderButtons/HeaderButtonsDefault';
import Header from '@/widgets/Header/ui/Header';
import { useGetHeaderOptions } from '@/shared/hooks/useGetHeaderOptions';
import { useLocale } from 'next-intl';
import { useGetGlobalOptions } from '@/shared/hooks/useGetGlobalOptions';
import { IImage } from '@/shared/api/types/image';

interface Props {}

export const PopularPageClient: FC<Props> = () => {
  console.log('[Client] PopularPageClient render start');

  const locale = useLocale();

  const [tag, setTag] = useQueryParam<PopularTypes | undefined>('date');
  const [category] = useQueryParam<string | undefined>('category');

  const { data: headerOptions } = useGetHeaderOptions({ lang: locale });
  const { data: globalOptions } = useGetGlobalOptions({ lang: locale });

  const tagsData = [
    {
      name: headerOptions?.settings_header_popular.for_month,
      type: PopularTypes.month,
    },
    {
      name: headerOptions?.settings_header_popular.for_week,
      type: PopularTypes.week,
    },
    {
      name: headerOptions?.settings_header_popular.for_all,
      type: PopularTypes.all,
    },
  ];

  const { data, isLoading, isFetchingNextPage, isRefetching, fetchNextPage } =
    useGetPopular({
      category_id: category ? Number.parseInt(category) : undefined,
      type: tag ?? PopularTypes.month,
      limit: 12,
      paged: 1,
      lang: locale,
    });

  const images = useMemo(() => {
    return data?.pages?.flatMap((page) => page.photos || []) ?? [];
  }, [data]);

  const onLoadMore = () => {
    if (!isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  const imageHrefSelector = useCallback(
    (image: IImage) => {
      const searchParams = new URLSearchParams();
      searchParams.set('siblings', 'popular');
      searchParams.set('date', tag ?? PopularTypes.month);

      if (category) {
        searchParams.set('category', category);
      }

      const searchParamsString = searchParams.toString();

      return `/image/${image.photoId}${searchParamsString ? `?${searchParamsString}` : ''}`;
    },
    [tag, category]
  );

  useEffect(() => {
    console.log('[Client] PopularPageClient mounted', {
      hasData: !!data,
      isLoading,
    });
  }, []);

  return (
    <Layout
      headerSlot={
        <Header
          buttonsSlot={<HeaderButtonsDefault />}
          afterSlot={
            <Tags className={cvaTags()}>
              {tagsData.map((el, i) => (
                <Tag
                  key={i}
                  theme={
                    el.type === (tag ?? PopularTypes.month)
                      ? 'active'
                      : 'blurred'
                  }
                  onClick={() => setTag(el.type)}>
                  {el.name}
                </Tag>
              ))}
            </Tags>
          }
        />
      }>
      <main className={cvaRoot()}>
        <PreviewImageItems
          isLoading={isLoading || isRefetching}
          images={images}
          empty={{ title: globalOptions?.settings_popular_no_results ?? '' }}
          isFavoriteIconAfter
          hrefSelector={imageHrefSelector}
          onLoadMore={onLoadMore}
        />
      </main>
    </Layout>
  );
};

const cvaRoot = cva([
  'PopularPageClient-cvaRoot',
  'mt-0.4 mb-1.6 md-max:mb-1.2 sm-max:mt-0.8 sm-max:mb-1.2',
]);

const cvaTags = cva([
  'PopularPageClient-cvaTags',
  'mt-1.2 md-max:mt-0.8 sm-max:mt-0.4',
]);
